import { useQuery } from '@tanstack/react-query';

import { searchUserPublicQuizsets } from '@/api/gen/quiz-user/quiz-user.gen';
import { useCountryCode } from '@/shared/hooks';
import { withServiceUnavailableRetry } from '@/shared/utils/queryRetryBudget';

/**
 * useOwnerQuizzes - 퀴즈 소유자의 다른 퀴즈 목록을 가져오는 hook
 *
 * @description
 * POST /users/{userId}/quizsets API를 호출하여
 * 특정 사용자(소유자)가 만든 공개 퀴즈 목록을 가져옵니다.
 * React Query의 useQuery를 사용하여 데이터를 캐싱하고 관리합니다.
 *
 * @param ownerId - 퀴즈 소유자 ID
 * @returns React Query result object
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useOwnerQuizzes('user-123');
 * ```
 */
export const useOwnerQuizzes = (ownerId: string) => {
  const { countryCode } = useCountryCode();

  return useQuery({
    queryKey: ['user-quizzes', ownerId, countryCode],
    ...withServiceUnavailableRetry({
      retry: 1, // 1회만 재시도
      queryFn: async ({ signal }) => {
        if (!countryCode) {
          throw new Error('countryCode is not available');
        }

        const response = await searchUserPublicQuizsets(
          ownerId,
          {
            limit: 10,
            page: 1,
            order: 'RECENTLY',
            rootOnly: false,
            categoryFilters: {
              criteria: {
                countryCode,
              },
            },
          },
          undefined,
          signal,
        );

        return response;
      },
    }),
    enabled: !!ownerId && !!countryCode,
    staleTime: 0,
    throwOnError: false, // 에러를 throw하지 않고 isError 상태로 처리
  });
};
